import React, { FC, ReactNode, createContext, useContext } from 'react';
import { ThemeProvider, useTheme } from '@airwallex/theme';
import type { ThemeProviderProps } from '@airwallex/theme';

/**
 * Locked-surface theme override for the RHS payment section (ADV-6447, PAA-3012).
 *
 * The payment element (the RHS) is themed by the merchant `colorBrand` ONLY —
 * `colorText` and `colorBackground` must not affect it. Its paper and ink are the
 * *resolved mode's* defaults, so a dark-mode checkout gets a dark payment column
 * rather than `@airwallex/theme`'s single light base palette. The surrounding
 * page (LHS) is themed with the full merchant palette by `ThemedBrandingShell`;
 * this context carries the locked override so the payment subtree can reset its
 * text and surfaces while keeping the brand accents.
 *
 * `undefined` means no brand color and no mode (un-themed, or the merchant set no
 * brand), in which case `PaymentThemeScope` is a transparent pass-through and
 * production renders bit-for-bit unchanged.
 */
type PaymentThemeOverride = ThemeProviderProps['themeOverride'];

type PaymentThemeValue = {
  // Whether the surrounding page is merchant-themed. When true, the RHS payment
  // subtree is re-themed from the resolved mode's defaults so it never inherits
  // the LHS order-info theme — even when there is no brand override to apply
  // (e.g. a merchant who set a background but no brand color).
  active: boolean;
  // The locked override applied on top of the base, or `undefined` to reset the
  // subtree to the plain FEC base.
  override?: PaymentThemeOverride;
  // The locked surface's resolved paper (background.primary), or `undefined` when
  // the page is un-themed.
  //
  // Some payment-column chrome is painted OUTSIDE this scope and so cannot read
  // the theme through context — notably the Xero checkout's fixed right gutter
  // (`desktopCss::before`), which is a page-level pseudo-element. Those callers
  // read the paper from here and paint it themselves so the gutter is not left
  // white behind a dark payment column (PAA-3012).
  surfaceBackground?: string;
};

const PaymentThemeContext = createContext<PaymentThemeValue>({ active: false });

export const PaymentThemeProvider: FC<{ value: PaymentThemeValue; children: ReactNode }> = ({ value, children }) => (
  <PaymentThemeContext.Provider value={value}>{children}</PaymentThemeContext.Provider>
);

// The payment subtree's theme intent: whether to re-theme, and the locked
// override to apply when doing so.
export const usePaymentTheme = (): PaymentThemeValue => useContext(PaymentThemeContext);

// The locked payment surface's paper, for chrome painted outside the scope. See
// `PaymentThemeValue.surfaceBackground`.
export const usePaymentSurfaceBackground = (): string | undefined => useContext(PaymentThemeContext).surfaceBackground;

// Re-themes the payment subtree (RHS) when the page is merchant-themed. A nested
// ThemeProvider rebuilds the theme from the resolved mode's default tokens — it
// does NOT inherit the parent (LHS) colorText/colorBackground theme — so the
// payment section's text and surfaces fall back to the neutral defaults and only
// the merchant brand accents remain (ADV-6447). langKey is inherited from the
// surrounding theme so non-English checkouts keep their localized typography.
// Pass-through (no extra DOM node, no theme change) when the page is un-themed.
export const PaymentThemeScope: FC<{ children: ReactNode }> = ({ children }) => {
  const { active, override } = usePaymentTheme();
  const { langKey } = useTheme();
  if (!active) return <React.Fragment>{children}</React.Fragment>;
  return (
    <ThemeProvider langKey={langKey} themeOverride={override}>
      {children}
    </ThemeProvider>
  );
};
